import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Gavel as LegalIcon,
} from '@mui/icons-material';

const ElectionsMunicipalesProject: React.FC = () => {
  return (
    <Box>
      {/* Header avec titre et image */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <img
          src="/TV/TF1EM.png"
          alt="Logo Elections Municipales TF1"
          style={{
            width: '64px',
            height: '64px',
            objectFit: 'contain',
            filter: 'drop-shadow(2px 2px 4px rgba(167, 139, 250, 0.3))',
          }}
        />
        <Typography
          variant="h4"
          component="h2"
          sx={{
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold'
          }}
        >
          Elections Municipales 2020
        </Typography>
      </Box>

      {/* Statut et date */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            2020
          </Typography>
        </Box>
      </Box>

      {/* Description */}
      <Typography
        variant="body1"
        sx={{ mb: 3, lineHeight: 1.6 }}
      >
        Projet d'envergure pour la couverture des Elections Municipales 2020 sur TF1 et LCI. 
        Mise à jour et adaptation des systèmes existants avec gestion complète du flux d'informations 
        depuis les instituts de sondage jusqu'à la diffusion, en respectant scrupuleusement 
        les contraintes légales de révélation des résultats.
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* Systèmes développés */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold'
          }}
        >
          Systèmes développés
        </Typography>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2
        }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Adaptation systèmes électoraux existants
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Interface instituts de sondage
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Serveur de temps
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Gestion automatisée du reveal
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Communication habillages antenne
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Diffusion TF1 émission spéciale
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Flux continu LCI 24h
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Monitoring temps réel
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Domaines techniques */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold'
          }}
        >
          Domaines techniques
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {['Systèmes électoraux', 'Intégration sondages', 'Serveurs de temps', 'Contraintes légales', 'Diffusion multi-canaux', 'Monitoring critique'].map((tech) => (
            <Chip
              key={tech}
              label={tech}
              variant="filled"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.1) 0%, rgba(20, 184, 166, 0.1) 100%)',
                color: '#a78bfa',
                border: '1px solid rgba(167, 139, 250, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.2) 0%, rgba(20, 184, 166, 0.2) 100%)',
                },
              }}
            />
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Défis techniques */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold'
          }}
        >
          Défis techniques relevés
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Contraintes légales critiques :</strong> Implementer un système de reveal 
          automatisé respectant scrupuleusement les horaires légaux de publication des résultats 
          électoraux, avec tolérance zéro aux erreurs de timing.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Intégration multi-sources :</strong> Orchestrer les flux de données provenant 
          de multiples instituts de sondage avec des formats et protocoles différents, 
          en garantissant la cohérence et la fiabilité des informations.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Diffusion multi-canaux :</strong> Gérer simultanément l'émission spéciale TF1 
          et le flux continu LCI 24h, avec adaptation automatique du contenu selon le contexte 
          de diffusion et les contraintes éditoriales.
        </Typography>
        <Typography variant="body2">
          <strong>Fiabilité nationale :</strong> Assurer une stabilité absolue pour un événement 
          démocratique majeur suivi par des millions de citoyens, avec des procédures de 
          récupération instantanée en cas d'incident.
        </Typography>
      </Box>
    </Box>
  );
};

export default ElectionsMunicipalesProject;
